/**
 * User Exercise Preference Service
 *
 * Service for managing user exercise tracking preferences including
 * creation, updating, listing, and deletion. Extends the base CrudService
 * with a single shape for both list and detail (index and show return
 * the same structure).
 */

import { ZodError } from 'zod';

import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { TRACKING_TYPE_LABEL_TO_VALUE } from '../utils/select-options';
import {
  validateCreateUserExercisePreference,
  validateUpdateUserExercisePreference,
  type CreateUserExercisePreferenceInput,
  type UpdateUserExercisePreferenceInput,
} from '../validation/user-exercise-preference.schemas';

import type { QueryParams } from '../types/api/query.types';
import type { ID } from '../types/common/id.types';
import type {
  ExercisePreferenceTrackingType,
  UserExercisePreference,
} from '../types/domain/user-preferences.types';

// =============================================================================
// USER EXERCISE PREFERENCE SERVICE CLASS
// =============================================================================

export class UserExercisePreferenceService extends CrudService<
  UserExercisePreference,
  UserExercisePreference,
  CreateUserExercisePreferenceInput
> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/exercise-preference',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  // ---------------------------------------------------------------------------
  // CORE PREFERENCE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get all exercise preferences for the current user.
   *
   * GET /api/exercise-preference
   */
  public async getExercisePreferences(
    params?: QueryParams
  ): Promise<UserExercisePreference[]> {
    try {
      return await this.getAll(params);
    } catch (error) {
      console.error('Failed to fetch exercise preferences:', error);
      rethrowTyped(
        error,
        'Unable to fetch exercise preferences. Please try again.'
      );
    }
  }

  /**
   * Get a single exercise preference by exercise GUID.
   *
   * GET /api/exercise-preference/{exercise_guid}
   */
  public async getExercisePreferenceByGuid(
    exerciseGuid: ID
  ): Promise<UserExercisePreference> {
    if (!exerciseGuid) {
      throw new Error('Exercise GUID is required to fetch a preference');
    }

    try {
      const preference = await this.getById(exerciseGuid);
      return this.transformResponse(preference);
    } catch (error) {
      console.error(
        `Failed to fetch exercise preference for ${exerciseGuid}:`,
        error
      );
      rethrowTyped(
        error,
        'Unable to fetch exercise preference. Please check the exercise GUID and try again.'
      );
    }
  }

  /**
   * Create a new exercise preference.
   *
   * POST /api/exercise-preference
   * Uses exercise-preference.schemas.ts for client-side validation.
   */
  public async createExercisePreference(
    data: CreateUserExercisePreferenceInput
  ): Promise<UserExercisePreference> {
    try {
      this.validateCreateData(data);

      const transformed = this.transformCreateRequest(data);
      const created = await this.create(transformed);

      return this.transformResponse(created);
    } catch (error) {
      console.error('Failed to create exercise preference:', error);
      rethrowTyped(
        error,
        'Unable to create exercise preference. Please check your data and try again.'
      );
    }
  }

  /**
   * Set how a movement is tracked, from the label the user picked.
   *
   * `hasPreference` says whether a preference row already exists. An exercise
   * whose tracking type was inferred has none, so the first choice creates it
   * and every later one updates it. The caller knows which from the exercise
   * detail's `tracking_type_is_inferred`; asking the API instead would use
   * GET /exercise-preference/{guid}, which writes a row as a side-effect of
   * reading.
   */
  public async setTrackingType(
    exerciseGuid: ID,
    trackingType: ExercisePreferenceTrackingType,
    hasPreference: boolean
  ): Promise<UserExercisePreference> {
    const tracking_type = TRACKING_TYPE_LABEL_TO_VALUE[trackingType];
    return hasPreference
      ? this.updateExercisePreference(exerciseGuid, { tracking_type })
      : this.createExercisePreference({
          exercise_guid: String(exerciseGuid),
          tracking_type,
        });
  }

  /**
   * Update an existing exercise preference by exercise GUID.
   *
   * PUT/PATCH /api/exercise-preference/{exercise_guid}
   * Uses exercise-preference.schemas.ts for client-side validation.
   */
  public async updateExercisePreference(
    exerciseGuid: ID,
    data: UpdateUserExercisePreferenceInput
  ): Promise<UserExercisePreference> {
    if (!exerciseGuid) {
      throw new Error('Exercise GUID is required to update a preference');
    }

    try {
      this.validateUpdateData(data);

      const transformed = this.transformUpdateRequest(data);
      const updated = await this.update(exerciseGuid, transformed, {
        partial: true,
      });

      return this.transformResponse(updated);
    } catch (error) {
      console.error(
        `Failed to update exercise preference for ${exerciseGuid}:`,
        error
      );
      rethrowTyped(
        error,
        'Unable to update exercise preference. Please check your data and try again.'
      );
    }
  }

  /**
   * Delete an exercise preference by exercise GUID.
   *
   * DELETE /api/exercise-preference/{exercise_guid}
   */
  public async deleteExercisePreference(exerciseGuid: ID): Promise<void> {
    if (!exerciseGuid) {
      throw new Error('Exercise GUID is required to delete a preference');
    }

    try {
      await this.delete(exerciseGuid);
    } catch (error) {
      console.error(
        `Failed to delete exercise preference for ${exerciseGuid}:`,
        error
      );
      rethrowTyped(
        error,
        'Unable to delete exercise preference. Please try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION
  // ---------------------------------------------------------------------------

  /**
   * Validate create payload using Zod schema.
   */
  protected validateCreateData(data: CreateUserExercisePreferenceInput): void {
    try {
      validateCreateUserExercisePreference(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod(
          "Couldn't save the exercise preference",
          error,
          {
            originalData: data,
            details: {
              service: 'UserExercisePreferenceService',
              operation: 'create',
            },
          }
        );
      }

      throw new InvalidDataError(
        "Couldn't save the exercise preference: the data is in an unexpected format",
        {
          originalData: data,
          details: {
            service: 'UserExercisePreferenceService',
            operation: 'create',
          },
        }
      );
    }
  }

  /**
   * Validate update payload using Zod schema.
   */
  protected validateUpdateData(data: UpdateUserExercisePreferenceInput): void {
    try {
      validateUpdateUserExercisePreference(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod(
          "Couldn't save the exercise preference",
          error,
          {
            originalData: data,
            details: {
              service: 'UserExercisePreferenceService',
              operation: 'update',
            },
          }
        );
      }

      throw new InvalidDataError(
        "Couldn't save the exercise preference: the data is in an unexpected format",
        {
          originalData: data,
          details: {
            service: 'UserExercisePreferenceService',
            operation: 'update',
          },
        }
      );
    }
  }

  /**
   * Transform create request payload before sending to API.
   * Currently passes data through unchanged.
   */
  protected transformCreateRequest(
    data: CreateUserExercisePreferenceInput
  ): CreateUserExercisePreferenceInput {
    return {
      ...data,
    };
  }

  /**
   * Transform update request payload before sending to API.
   * Currently passes data through unchanged.
   */
  protected transformUpdateRequest(
    data: UpdateUserExercisePreferenceInput
  ): UpdateUserExercisePreferenceInput {
    return {
      ...data,
    };
  }

  /**
   * Normalize response from API for frontend consumption.
   * Currently returns data as-is.
   */
  protected override transformResponse(
    data: UserExercisePreference
  ): UserExercisePreference {
    return {
      ...data,
    };
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default user exercise preference service instance.
 */
export const userExercisePreferenceService =
  new UserExercisePreferenceService();
